/**
 * Triangle SVG Renderer
 * Handles dynamic SVG generation for the triangle navigation widget
 */

import {
  TRIANGLE_PATH,
  SIZES,
  CENTER_POSITIONS,
  AREA_CENTER_OFFSETS,
  START_CIRCLES,
  PERIPHERAL_CIRCLES,
  CIRCLE_RANGES,
  COLORS,
  STATES,
} from './constants.js'

export default class TriangleRenderer {
  constructor(container) {
    this.container = container
    this.svg = null
    this.elements = {
      centerCircle: null,
      centerCircleMasked: null,
      peripheralCirclesGroup: null,
      triangle: null,
      backButton: null,
      closeButton: null,
    }
  }

  /**
   * Initialize the SVG structure
   */
  init() {
    this.svg = this.createSVGElement('svg', {
      viewBox: '0 0 247 242',
      'data-triangle-nav': '',
    })

    // Create defs for mask
    const defs = this.createSVGElement('defs')
    const mask = this.createSVGElement('mask', { id: 'triangle-mask' })
    const maskRect = this.createSVGElement('rect', {
      x: 0,
      y: 0,
      width: 247,
      height: 242,
      fill: 'black',
    })
    const maskPath = this.createSVGElement('path', {
      d: TRIANGLE_PATH,
      fill: 'white',
    })
    mask.appendChild(maskRect)
    mask.appendChild(maskPath)
    defs.appendChild(mask)
    this.svg.appendChild(defs)

    // Create triangle shape FIRST (bottom layer)
    this.elements.triangle = this.createSVGElement('path', {
      class: 'triangle-shape',
      d: TRIANGLE_PATH,
      fill: COLORS.triangleFill,
    })
    this.svg.appendChild(this.elements.triangle)

    // Create center circle (outside triangle) - dark purple, same as triangle
    this.elements.centerCircle = this.createSVGElement('circle', {
      class: 'center-circle',
      cx: CENTER_POSITIONS.start.cx,
      cy: CENTER_POSITIONS.start.cy,
      r: SIZES.centerCircle,
      fill: COLORS.triangleFill, // Dark purple (#350F4B) to blend with triangle
    })
    this.svg.appendChild(this.elements.centerCircle)

    // Create center circle masked (inside triangle) - light purple to match widget background
    this.elements.centerCircleMasked = this.createSVGElement('circle', {
      class: 'center-circle-masked',
      cx: CENTER_POSITIONS.start.cx,
      cy: CENTER_POSITIONS.start.cy,
      r: SIZES.centerCircle,
      fill: COLORS.centerCircleFill, // Light purple (#E5D8FF) to show inside triangle
      mask: 'url(#triangle-mask)',
    })
    this.svg.appendChild(this.elements.centerCircleMasked)

    // Create peripheral circles group
    this.elements.peripheralCirclesGroup = this.createSVGElement('g', {
      class: 'peripheral-circles',
    })
    this.svg.appendChild(this.elements.peripheralCirclesGroup)

    // Add to container
    this.container.appendChild(this.svg)

    return this
  }

  /**
   * Render circles based on current state
   * @param {Object} state - Current navigation state
   * @param {Object} data - Navigation data from CMS
   * @param {Object} previousState - Previous navigation state (optional, for animations)
   */
  renderState(state, data, _previousState = null) {
    switch (state.type) {
      case STATES.START:
        this.updateCirclesForStart(data)
        break
      case STATES.DIMENSION:
        this.updateCirclesForDimension(state.dimension, data)
        break
      case STATES.AREA:
        this.updateCirclesForArea(state.dimension, state.area, data)
        break
    }
  }

  /**
   * Update circles for area state
   */
  updateCirclesForArea(dimensionHandle, areaHandle, data) {
    this.clearPeripheralCircles()
    this.renderAreaCircles(dimensionHandle, areaHandle, data)
  }

  /**
   * Update circles for dimension state
   */
  updateCirclesForDimension(dimensionHandle, data) {
    this.clearPeripheralCircles()
    this.renderDimensionCircles(dimensionHandle, data)
  }

  /**
   * Update circles for start state
   */
  updateCirclesForStart(data) {
    this.clearPeripheralCircles()
    this.renderStartCircles(data)
  }

  /**
   * Render start state dimension circles (3 circles with r=12)
   */
  renderStartCircles(data) {
    START_CIRCLES.forEach(circleData => {
      const dimension = data.dimensions?.find(d => d.handle === circleData.handle)
      if (!dimension) return

      const group = this.createCircleGroup(
        circleData,
        dimension.title,
        dimension.url,
        false, // not active
        SIZES.dimensionCircle // r=12 for homepage dimension circles
      )
      this.elements.peripheralCirclesGroup.appendChild(group)
    })
  }

  /**
   * Render dimension area circles (always all 11 circles with r=7)
   * Maps each circle to the correct dimension's areas
   */
  renderDimensionCircles(dimensionHandle, data) {
    // Loop through ALL 11 circles and map each to its dimension's areas
    PERIPHERAL_CIRCLES.forEach((circleData, globalIndex) => {
      // Find which dimension this circle belongs to
      let circleDimension = null
      let localIndex = 0

      for (const [dimHandle, range] of Object.entries(CIRCLE_RANGES)) {
        if (globalIndex >= range.start && globalIndex <= range.end) {
          circleDimension = dimHandle
          localIndex = globalIndex - range.start // Position within this dimension's circles
          break
        }
      }

      if (!circleDimension) return

      // Get areas for this circle's dimension
      const dimensionAreas = data.areas?.[circleDimension] || []
      const area = dimensionAreas[localIndex] // Map by position within dimension

      const group = this.createCircleGroup(
        { ...circleData, handle: area?.handle },
        area?.title || '',
        area?.url || '#',
        false, // not active
        SIZES.peripheralCircle, // r=7 for area circles
        !area, // isPlaceholder - not yet created in CMS
        area?.parentDimensionUrl // Parent dimension URL for back navigation
      )
      this.elements.peripheralCirclesGroup.appendChild(group)
    })
  }

  /**
   * Render area circles (always all 11, with one active at r=25)
   * Maps each circle to the correct dimension's areas
   */
  renderAreaCircles(dimensionHandle, areaHandle, data) {
    // Loop through ALL 11 circles and map each to its dimension's areas
    PERIPHERAL_CIRCLES.forEach((circleData, globalIndex) => {
      // Find which dimension this circle belongs to
      let circleDimension = null
      let localIndex = 0

      for (const [dimHandle, range] of Object.entries(CIRCLE_RANGES)) {
        if (globalIndex >= range.start && globalIndex <= range.end) {
          circleDimension = dimHandle
          localIndex = globalIndex - range.start // Position within this dimension's circles
          break
        }
      }

      if (!circleDimension) return

      // Get areas for this circle's dimension
      const dimensionAreas = data.areas?.[circleDimension] || []
      const area = dimensionAreas[localIndex] // Map by position within dimension

      const isActive = area?.handle === areaHandle
      const radius = isActive ? SIZES.activeCircle : SIZES.peripheralCircle // r=25 for active, r=7 for others

      const group = this.createCircleGroup(
        { ...circleData, handle: area?.handle },
        area?.title || '',
        area?.url || '#',
        isActive,
        radius,
        !area, // isPlaceholder - not yet created in CMS
        area?.parentDimensionUrl // Parent dimension URL for back navigation
      )
      this.elements.peripheralCirclesGroup.appendChild(group)
    })
  }

  /**
   * Create a circle group with link and title
   * @param {Object} circleData - Circle position and id
   * @param {string} title - Circle title/label
   * @param {string} url - Link URL
   * @param {boolean} isActive - Whether circle is active
   * @param {number} radius - Circle radius (7, 12, or 25)
   * @param {boolean} isPlaceholder - True if area not yet created in CMS
   * @param {string} parentDimensionUrl - URL of parent dimension (for back navigation)
   */
  createCircleGroup(
    circleData,
    title,
    url,
    isActive,
    radius = SIZES.peripheralCircle,
    isPlaceholder = false,
    parentDimensionUrl = null
  ) {
    const group = this.createSVGElement('g', {
      class: `circle-group${isPlaceholder ? ' is-placeholder' : ''}${
        isActive ? ' is-active' : ''
      }`,
    })

    // Only create link if not a placeholder
    if (!isPlaceholder && circleData.handle) {
      const linkAttrs = {
        href: url,
        'data-handle': circleData.handle,
        'data-type': circleData.type || 'area',
        'aria-label': isActive ? `Close ${title} and return to dimension` : title,
      }

      // Add parent dimension URL for active circles (for back navigation)
      if (isActive && parentDimensionUrl) {
        linkAttrs['data-parent-dimension-url'] = parentDimensionUrl
      }

      const link = this.createSVGElement('a', linkAttrs)

      const circle = this.createSVGElement('circle', {
        cx: circleData.cx,
        cy: circleData.cy,
        r: radius,
        fill: isActive ? COLORS.circleFillActive : COLORS.circleFillInactive,
        stroke: COLORS.circleStroke,
        'stroke-width': 2,
        'data-handle': circleData.handle,
        'data-active': isActive,
      })

      const titleElement = this.createSVGElement('title')
      titleElement.textContent = title

      link.appendChild(circle)
      link.appendChild(titleElement)

      // Add close button X icon for active circles
      if (isActive && radius === SIZES.activeCircle) {
        const closeButton = this.createCloseButton(circleData.cx, circleData.cy)
        link.appendChild(closeButton)
      }

      group.appendChild(link)

      // Add tooltip for area circles (r=7) and dimension circles (r=12)
      if ((radius === 7 || radius === SIZES.dimensionCircle) && title) {
        const tooltip = this.createTooltip(title, circleData.cx, circleData.cy)
        group.appendChild(tooltip)
      }
    } else {
      // Placeholder circle (no link, no interactivity)
      const circle = this.createSVGElement('circle', {
        cx: circleData.cx,
        cy: circleData.cy,
        r: radius,
        fill: COLORS.circleFillInactive,
        stroke: COLORS.circleStroke,
        'stroke-width': 2,
        'data-placeholder': 'true',
        class: 'placeholder-circle',
      })

      group.appendChild(circle)
    }

    return group
  }

  /**
   * Create tooltip element for circle (pure SVG - no foreignObject)
   */
  createTooltip(text, cx, cy) {
    const tooltip = this.createSVGElement('g', {
      class: 'circle-tooltip',
      'data-tooltip': '',
    })

    // Create text element first to measure it
    const textElement = this.createSVGElement('text', {
      class: 'tooltip-text',
      'text-anchor': 'middle',
      'dominant-baseline': 'middle',
    })
    textElement.textContent = text

    // Create background rect
    const rect = this.createSVGElement('rect', {
      class: 'tooltip-background',
      rx: 8,
      ry: 8,
    })

    // Add to group (rect first so text is on top)
    tooltip.appendChild(rect)
    tooltip.appendChild(textElement)

    // Position tooltip group at circle center
    tooltip.setAttribute('transform', `translate(${cx}, ${cy})`)

    // Measure and position after render
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Use SVG's native getBBox for accurate measurement
        const textBBox = textElement.getBBox()

        // Padding in SVG units (20px left/right, 10px top/bottom)
        const paddingX = 20
        const paddingY = 10

        // Calculate rect dimensions
        const width = textBBox.width + (paddingX * 2)
        const height = textBBox.height + (paddingY * 2)

        // Position rect centered above circle
        const rectX = -width / 2
        const rectY = -40 - height / 2

        rect.setAttribute('x', rectX)
        rect.setAttribute('y', rectY)
        rect.setAttribute('width', width)
        rect.setAttribute('height', height)

        // Position text centered in rect
        textElement.setAttribute('x', 0)
        textElement.setAttribute('y', rectY + (height / 2))

        // Mark as ready for CSS transitions
        requestAnimationFrame(() => {
          tooltip.classList.add('is-ready')
        })
      })
    })

    return tooltip
  }

  /**
   * Adjust tooltip position to keep it within viewport boundaries
   * @param {SVGElement} tooltip - Tooltip element
   * @param {number} cx - Circle center X in SVG coordinates
   * @param {number} cy - Circle center Y in SVG coordinates
   * @param {number} tooltipWidth - Width of tooltip
   * @param {number} tooltipHeight - Height of tooltip
   * @param {number} initialOffsetY - Initial Y offset from circle center
   */
  adjustTooltipPosition(tooltip, cx, cy, tooltipWidth, tooltipHeight, initialOffsetY = -25) {
    // Get SVG element and its position in viewport
    const svgRect = this.svg.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    // Calculate SVG coordinate to screen coordinate scale
    const viewBox = this.svg.viewBox.baseVal
    const scaleX = svgRect.width / viewBox.width
    const scaleY = svgRect.height / viewBox.height

    // Use the provided offset (don't reset it!)
    let offsetY = initialOffsetY
    let offsetX = 0

    // Convert tooltip dimensions from SVG coordinates to screen coordinates
    const screenTooltipWidth = tooltipWidth * scaleX
    const screenTooltipHeight = tooltipHeight * scaleY

    // Calculate tooltip position in screen coordinates
    const tooltipScreenX = svgRect.left + cx * scaleX
    const tooltipScreenY = svgRect.top + (cy + offsetY) * scaleY

    // Check if tooltip goes off the top of viewport
    if (tooltipScreenY - screenTooltipHeight / 2 < 0) {
      // Flip tooltip to below circle
      offsetY = 25 + tooltipHeight / 2
    }

    // Check if tooltip goes off the left of viewport
    if (tooltipScreenX - screenTooltipWidth / 2 < 0) {
      // Shift tooltip right
      const overflow = -(tooltipScreenX - screenTooltipWidth / 2)
      offsetX = overflow / scaleX
    }

    // Check if tooltip goes off the right of viewport
    if (tooltipScreenX + screenTooltipWidth / 2 > viewportWidth) {
      // Shift tooltip left
      const overflow = tooltipScreenX + screenTooltipWidth / 2 - viewportWidth
      offsetX = -overflow / scaleX
    }

    // Apply adjusted position
    tooltip.setAttribute('transform', `translate(${cx + offsetX}, ${cy + offsetY})`)
  }

  /**
   * Create close button X icon for active circles
   * @param {number} cx - Circle center X
   * @param {number} cy - Circle center Y
   */
  createCloseButton(cx, cy) {
    const closeButton = this.createSVGElement('g', {
      class: 'close-button',
      'data-close-button': '',
    })

    // Create two diagonal lines forming an X
    // X icon is 12x12px centered on the circle
    const iconSize = 6 // Half of 12px

    // Line from top-left to bottom-right
    const line1 = this.createSVGElement('line', {
      x1: cx - iconSize,
      y1: cy - iconSize,
      x2: cx + iconSize,
      y2: cy + iconSize,
      stroke: COLORS.centerCircleMasked, // Off-white color
      'stroke-width': 2,
      'stroke-linecap': 'round',
    })

    // Line from top-right to bottom-left
    const line2 = this.createSVGElement('line', {
      x1: cx + iconSize,
      y1: cy - iconSize,
      x2: cx - iconSize,
      y2: cy + iconSize,
      stroke: COLORS.centerCircleMasked, // Off-white color
      'stroke-width': 2,
      'stroke-linecap': 'round',
    })

    closeButton.appendChild(line1)
    closeButton.appendChild(line2)

    return closeButton
  }

  /**
   * Clear all peripheral circles
   */
  clearPeripheralCircles() {
    while (this.elements.peripheralCirclesGroup.firstChild) {
      this.elements.peripheralCirclesGroup.removeChild(
        this.elements.peripheralCirclesGroup.firstChild
      )
    }
  }

  /**
   * Get center circle position for a dimension/area
   * @param {string} dimensionHandle - Dimension handle
   * @param {string} areaHandle - Optional area handle for fine-tuned positioning
   * @param {Object} data - Navigation data to find area index
   */
  getCenterPosition(dimensionHandle, areaHandle = null, data = null) {
    const basePosition = CENTER_POSITIONS[dimensionHandle] || CENTER_POSITIONS.start

    // If no area specified, return base dimension position
    if (!areaHandle || !data) {
      return basePosition
    }

    // Get area index within this dimension
    const dimensionAreas = data.areas?.[dimensionHandle] || []
    const areaIndex = dimensionAreas.findIndex(a => a.handle === areaHandle)

    if (areaIndex === -1) {
      return basePosition
    }

    // Get offset for this area
    const offsets = AREA_CENTER_OFFSETS[dimensionHandle]
    if (!offsets || !offsets[areaIndex]) {
      return basePosition
    }

    // Apply offset to base position
    const offset = offsets[areaIndex]
    return {
      cx: offset.cx !== undefined ? offset.cx : basePosition.cx,
      cy: offset.cy !== undefined ? offset.cy : basePosition.cy,
    }
  }

  /**
   * Create SVG element with attributes
   */
  createSVGElement(tag, attrs = {}) {
    const element = document.createElementNS('http://www.w3.org/2000/svg', tag)
    Object.entries(attrs).forEach(([key, value]) => {
      if (key === 'class') {
        element.setAttribute('class', value)
      } else {
        element.setAttribute(key, value)
      }
    })
    return element
  }

  /**
   * Get all circle elements for animation
   */
  getPeripheralCircles() {
    return Array.from(this.elements.peripheralCirclesGroup.querySelectorAll('circle'))
  }

  /**
   * Get center circle elements
   */
  getCenterCircles() {
    return [this.elements.centerCircle, this.elements.centerCircleMasked]
  }

  /**
   * Remeasure all tooltips - call this when the SVG becomes visible
   * after being hidden with display:none (getBBox returns 0 when hidden)
   */
  remeasureTooltips() {
    if (!this.svg) return

    const tooltips = this.svg.querySelectorAll('[data-tooltip]')

    tooltips.forEach(tooltip => {
      const textElement = tooltip.querySelector('.tooltip-text')
      const rect = tooltip.querySelector('.tooltip-background')

      if (!textElement || !rect) return

      // Use requestAnimationFrame to ensure element is rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const textBBox = textElement.getBBox()

          // Skip if still returning zero (still hidden)
          if (textBBox.width === 0) return

          const paddingX = 20
          const paddingY = 10

          const width = textBBox.width + (paddingX * 2)
          const height = textBBox.height + (paddingY * 2)

          const rectX = -width / 2
          const rectY = -40 - height / 2

          rect.setAttribute('x', rectX)
          rect.setAttribute('y', rectY)
          rect.setAttribute('width', width)
          rect.setAttribute('height', height)

          textElement.setAttribute('x', 0)
          textElement.setAttribute('y', rectY + (height / 2))

          // Mark as ready if not already
          if (!tooltip.classList.contains('is-ready')) {
            requestAnimationFrame(() => {
              tooltip.classList.add('is-ready')
            })
          }
        })
      })
    })
  }
}
